import { AnalyticsStore } from 'bernie-plugin-mobx';
import {
  ActivityDetailBasic, ActivityDetailOffers,
  ActivityOfferUdsToken,
  ActivitySelectedValueInput
} from '../common/__generated__/api';
import { CurrentTicketCount } from '../components/activity-details/offer/ticket';
import { ActivityDetailStore, DetailCriteriaStore } from '../stores';
import { STORE_EVENTS } from '../stores/store-events';
import { addIfNotPresent } from './common-helper';
import OffersSummary = ActivityDetailOffers.OffersSummary;

const LANGUAGE = 'language';
const START_TIME = 'startTime';

function getOfferSelectionById(offer: ActivityDetailOffers.Offers, id: string): ActivityDetailOffers.Selections | null {
  const selections: ActivityDetailOffers.Selections[] =
    offer.selections && offer.selections.length > 0
      ? offer.selections.filter((selection: ActivityDetailOffers.Selections) => selection.id === id)
      : [];

  return selections.length > 0 ? (selections.pop() as ActivityDetailOffers.Selections) : null;
}

export const saveOfferFilters = (
  offerFilterMap: Map<string, ActivitySelectedValueInput> | null,
  offers: ActivityDetailOffers.Offers[]
): Map<string, ActivitySelectedValueInput> => {
  if (!offerFilterMap) {
    offerFilterMap = new Map<string, ActivitySelectedValueInput>();
  }

  offers.forEach(offer => {
    const languageSelection = getOfferSelectionById(offer, LANGUAGE);

    const fields =
      languageSelection &&
      languageSelection.fields &&
      languageSelection.fields.length > 0 &&
      languageSelection.fields.filter(setting => setting.theme === ActivityOfferUdsToken.SELECTED);

    const field = fields && fields.length > 0 && fields.pop();

    if (field && offerFilterMap) {
      offerFilterMap.set(offer.id, {
        id: (languageSelection as ActivityDetailOffers.Selections).id,
        value: field.value
      });
    }
  });

  return offerFilterMap;
};

function populateFilterWithTicketSelection(
  currentTicketSelection: CurrentTicketCount[] | undefined,
  detailCriteriaStore: DetailCriteriaStore,
  activityDetailStore: ActivityDetailStore
): void {
  if (currentTicketSelection) {
    detailCriteriaStore.offerFilters.tickets = [];
    currentTicketSelection.forEach(ticket => {
      const ticketCount = ticket.currentCount;
      try {
        if (detailCriteriaStore.offerFilters.tickets) {
          detailCriteriaStore.offerFilters.tickets.push({ code: ticket.ticketType.label, count: ticketCount });
        }
      } catch (error) {
        activityDetailStore.failureHandler(null, STORE_EVENTS.DETAIL_OFFER_FILTER_TICKET_DATA_PARSE_ERROR, error);
      }
    });
  }
}

export function resetFilters(detailCriteriaStore: DetailCriteriaStore): void {
  detailCriteriaStore.offerFilters = {};
}

export const updateDetailCriteriaStore = (
  field: ActivityDetailBasic.Fields,
  activityDetailStore: ActivityDetailStore,
  detailCriteriaStore: DetailCriteriaStore,
  option: ActivityDetailBasic.Filters,
  currentTicketSelection?: CurrentTicketCount[],
  offerId?: string
): void => {
  resetFilters(detailCriteriaStore);

  const mealInclusions = activityDetailStore.getOfferMealInclusions();

  let existingFilters: ActivitySelectedValueInput[] = [];

  if (mealInclusions) {
    existingFilters = [...mealInclusions];
  }

  const filter: ActivitySelectedValueInput = {
    id: option.id,
    value: field.value
  };

  if (option && option.id === 'mealInclusion') {
    if (field.theme === ActivityOfferUdsToken.UNSELECTED) {
      // Adding new selection
      addIfNotPresent(existingFilters, filter);
    } else {
      // Removing matching selection
      existingFilters = existingFilters.filter(existingFilter => existingFilter.value !== field.value);
    }

    activityDetailStore.setOfferMealInclusions(existingFilters);
  } else if (field.theme === ActivityOfferUdsToken.UNSELECTED) {
    const filterMap = option && START_TIME === option.id ? activityDetailStore.getOfferFiltersMap() : null;
    const existingLanguageFilter = filterMap && offerId ? filterMap.get(offerId) : null;
    if (existingLanguageFilter) {
      addIfNotPresent(existingFilters, existingLanguageFilter);
    }
    addIfNotPresent(existingFilters, filter);
  }

  if (offerId) {
    detailCriteriaStore.offerId = offerId;
  }

  if (existingFilters.length > 0) {
    detailCriteriaStore.offerFilters.selections = existingFilters;
  }

  populateFilterWithTicketSelection(currentTicketSelection, detailCriteriaStore, activityDetailStore);
};

export const trackOfferFilterClickEvent = (
  field: ActivityDetailBasic.Fields,
  option: ActivityDetailBasic.Filters,
  analytics: AnalyticsStore
): void => {
  const filterId = option && option.id;
  const fieldName = field && field.value;
  const fieldSelectedValue =
    field && field.theme && field.theme === ActivityOfferUdsToken.SELECTED ? 'UNSELECTED' : 'SELECTED';
  if (filterId && fieldName && fieldSelectedValue) {
    analytics.trackEvent(
      `LX.IS.${filterId}.${fieldName}.${fieldSelectedValue}`,
      `Selecting offer filter ${field.value}`,
      analytics.omnitureData
    );
  }
};

function trackOfferFilterBasedOnState(filterName: string, state: string, analytics: AnalyticsStore): void {
  const rfrr = `LX.IS.${filterName}.${state}`;
  const linkName = `Filter ${filterName} is ${state}`;
  analytics.trackEvent(rfrr, linkName, analytics.omnitureData);
}

function getState(isSelectionPresent: boolean): string {
  return isSelectionPresent ? 'present' : 'absent';
}

function isSelectionByIdPresentInOfferList(offers: ActivityDetailOffers.Offers[], id: string): boolean {
  return (
    offers &&
    offers.some(
      (offer: ActivityDetailOffers.Offers) =>
        offer.selections &&
        offer.selections.some((selection: ActivityDetailOffers.Selections): boolean => selection.id === id)
    )
  );
}

function trackOfferFilterPresentOrAbsent(offers: ActivityDetailOffers.Offers[], analytics: AnalyticsStore): void {
  if (offers && offers.length > 0) {
    const offerLanguageSelectionPresent = isSelectionByIdPresentInOfferList(offers, LANGUAGE);
    const offerStartTimeSelectionPresent = isSelectionByIdPresentInOfferList(offers, START_TIME);

    trackOfferFilterBasedOnState(LANGUAGE, getState(offerLanguageSelectionPresent), analytics);
    trackOfferFilterBasedOnState(START_TIME, getState(offerStartTimeSelectionPresent), analytics);
  }
}

function trackMealInclusionsPresentOrNot(offerSummary: OffersSummary | undefined, analytics: AnalyticsStore): void {
  let state = 'absent';

  const isMealInclusionsPresent =
    offerSummary &&
    offerSummary.filters &&
    offerSummary.filters.some(filter => filter.fields && filter.fields.length > 0);

  if (isMealInclusionsPresent) {
    state = 'present';
  }

  trackOfferFilterBasedOnState('mealInclusion', state, analytics);
}

export const trackOfferFilterEventOnPageLoad = (
  analytics: AnalyticsStore,
  offers: ActivityDetailOffers.Offers[],
  offerSummary?: OffersSummary
): void => {
  const offersWithNoFilter =
    offers.length > 0
      ? offers.filter(offer => offer.selections === null || (offer.selections && offer.selections.length === 0))
      : [];

  if (offersWithNoFilter && offersWithNoFilter.length === offers.length) {
    analytics.trackEvent('LX.IS.OfferConsolidation.fail', 'Offer consolidation fail.', analytics.omnitureData);
  } else {
    trackOfferFilterPresentOrAbsent(offers, analytics);
    trackMealInclusionsPresentOrNot(offerSummary, analytics);
  }
};
